import { Button } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { ButtonProps, GestureResponderEvent, StyleSheet, ViewStyle } from 'react-native';

import useTraceLoading from 'hooks/use-trace-loading';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { t } from 'state/intl/translations';
import { getTextFromChildren } from 'utils/get-text-from-children';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

type AttributesMap = { [key: string]: string | number | null };

interface IActionButtonProps extends Omit<ButtonProps, 'onPress' | 'title'> {
  eventAttributes?: AttributesMap;
  eventName?: CustomEventNames;
  eventType?: EventTypes;
  onPress?: (ev: GestureResponderEvent) => void;
  onNonVisualPress?: (ev: GestureResponderEvent) => void;
  skipLogEvent?: boolean;
  isLoading?: boolean;
  noLogOnLongLoading?: boolean;
  testID?: string;
  to?: string;
  linkPath?: string;
  perceptible?: boolean;
  isImperceptible?: boolean;
  hapticFeedbackStyle?: HapticsImpactStyle;
  children?: React.ReactNode;
  tabIndex?: number;
  fullWidth?: boolean;
  width?: string;
  customStyles?: ViewStyle;
}

// TODO: This is a temporary component that will be replaced with the new `Button` component. We are going to stop using UCL components in the app.
export const ActionButton = ({
  onPress,
  skipLogEvent = false,
  isLoading = false,
  isImperceptible,
  perceptible,
  disabled,
  onNonVisualPress,
  eventName,
  eventType,
  eventAttributes = {},
  children,
  hapticFeedbackStyle,
  linkPath,
  to,
  tabIndex = 0,
  noLogOnLongLoading,
  testID,
  fullWidth,
  width,
  customStyles,
}: IActionButtonProps) => {
  const buttonText = t('change');
  const buttonName = eventName || getTextFromChildren(children) || testID;
  const { navigate } = useRouter();
  const focusable = !(isLoading || isImperceptible || tabIndex === -1);
  const isLoadingOrLoggingDisabled = noLogOnLongLoading ? false : isLoading;

  useTraceLoading({
    name: buttonName,
    isLoading: isLoadingOrLoggingDisabled,
  });

  const handlePress = (e: GestureResponderEvent) => {
    if (isLoading || isImperceptible || (perceptible && disabled)) {
      e.preventDefault();
      if (perceptible && disabled) {
        void onNonVisualPress?.(e);
      }
      return;
    }

    if (!skipLogEvent) {
      try {
        const isCustomEvent = eventName && eventName !== CustomEventNames.BUTTON_CLICK;
        if (isCustomEvent) {
          logEvent(eventName, eventType || EventTypes.Navigation, {
            Name: eventName,
            ...eventAttributes,
          });
        } else {
          logButtonClick({
            attributes: {
              ...eventAttributes,
              component: ClickEventComponentNames.BUTTON,
              Name: getTextFromChildren(children),
              name: getTextFromChildren(children),
            },
          });
        }
      } catch (_) {
        // Handle error silently
      }
    }

    if (hapticFeedbackStyle) {
      hapticImpact({ style: hapticFeedbackStyle });
    }

    onPress?.(e);

    const navigationPath = linkPath || to;
    if (navigationPath) {
      navigate(navigationPath);
    }
  };

  return (
    <Button
      variant="outline"
      accessibilityLabel={buttonText}
      testID="change-btn"
      onPress={handlePress}
      style={[styles.button, customStyles]}
      _text={styles.text}
      isLoading={isLoading}
      dd-action-name={buttonName}
      focusable={focusable}
      isDisabled={!!disabled}
      width={fullWidth ? 'full' : width}
    >
      {buttonText}
    </Button>
  );
};

const styles = StyleSheet.create({
  button: {
    padding: 8,
    paddingLeft: 12,
    paddingRight: 12,
  },
  text: {
    fontSize: 12,
  },
});
